import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flag, Loader2, Sparkles, CheckCircle2, Trophy } from "lucide-react";
import { UserAvatar, UserLink } from "@/components/UserBits";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const daysLeft = (iso) => Math.max(0, Math.ceil((new Date(iso) - Date.now()) / 86400000));

const MEDAL = ["text-amber-500", "text-slate-400", "text-orange-400"];
const timeOf = (iso) => new Date(iso).toLocaleString("de-DE", { weekday: "short", hour: "2-digit", minute: "2-digit" });

export function ChallengeLeaderboard({ prefix = "" }) {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/challenge/leaderboard").then(({ data }) => setD(data)).catch(() => {}), []);
  useEffect(() => {
    load();
    window.addEventListener("points-changed", load);
    return () => window.removeEventListener("points-changed", load);
  }, [load]);
  if (!d) return null;
  return (
    <div className="bg-white rounded-3xl p-5 border border-pink-100 space-y-3" data-testid={`${prefix}challenge-leaderboard`}>
      <p className="font-bold flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-500" /> Schnellste Challenge-Profis</p>
      {d.top.length === 0
        ? <p className="text-sm text-slate-500" data-testid={`${prefix}challenge-leaderboard-empty`}>Noch hat niemand die Challenge geschafft – sei die/der Erste! 🏁</p>
        : <ol className="space-y-2.5">
            {d.top.map((u) => (
              <li key={u.user_id} className="flex items-center gap-3" data-testid={`${prefix}challenge-rank-${u.rank}`}>
                <span className={`w-5 text-center font-extrabold ${MEDAL[u.rank - 1] || "text-slate-300"}`}>{u.rank}</span>
                <UserAvatar userId={u.user_id} name={u.name} picture={u.picture} size="w-8 h-8" text="text-xs" />
                <UserLink id={u.user_id} className="flex-1 min-w-0 truncate text-sm font-semibold">{u.name}</UserLink>
                <span className="text-[11px] text-slate-400 shrink-0">{timeOf(u.claimed_at)}</span>
              </li>
            ))}
          </ol>}
      {d.my_rank && d.my_rank > 5 && <p className="text-xs text-slate-500" data-testid={`${prefix}challenge-my-rank`}>Du bist auf Platz {d.my_rank} von {d.total}</p>}
    </div>
  );
}

export function ChallengeWidget({ prefix = "" }) {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const { refreshUser } = useAuth();
  const load = useCallback(() => api.get("/challenge").then(({ data }) => setC(data)).catch(() => {}), []);
  useEffect(() => {
    load();
    window.addEventListener("points-changed", load);
    return () => window.removeEventListener("points-changed", load);
  }, [load]);
  if (!c) return null;
  const claim = async () => {
    setBusy(true);
    try { const { data } = await api.post("/challenge/claim"); toast.success(`Wochen-Challenge geschafft! +${data.earned} Punkte 🏆`); window.dispatchEvent(new Event("points-changed")); refreshUser(); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const left = daysLeft(c.ends_at);
  return (
    <div className="relative overflow-hidden rounded-3xl p-5 text-white bg-gradient-to-br from-fuchsia-500 via-pink-500 to-rose-500 shadow-lg shadow-rose-200" data-testid={`${prefix}challenge-widget`}>
      <span className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-white/10" />
      <p className="relative text-[11px] uppercase tracking-widest font-bold text-white/80 flex items-center gap-1.5"><Flag className="w-3.5 h-3.5" /> Wochen-Challenge · noch {left} {left === 1 ? "Tag" : "Tage"}</p>
      <Link to={c.url} className="relative block mt-1.5 font-display text-lg font-extrabold leading-snug hover:underline" data-testid={`${prefix}challenge-title`}>{c.title}</Link>
      <div className="relative mt-3 flex items-center gap-3">
        <div className="flex-1 h-2.5 rounded-full bg-white/25 overflow-hidden"><div className="h-full bg-white rounded-full transition-[width] duration-700" style={{ width: `${(c.progress / c.goal) * 100}%` }} /></div>
        <span className="text-sm font-bold" data-testid={`${prefix}challenge-progress`}>{c.progress}/{c.goal}</span>
      </div>
      {c.claimed
        ? <p className="relative mt-3 text-sm font-bold flex items-center gap-1.5" data-testid={`${prefix}challenge-claimed`}><CheckCircle2 className="w-4 h-4" /> +{c.bonus} Punkte abgeholt</p>
        : <button onClick={claim} disabled={!c.done || busy} data-testid={`${prefix}challenge-claim-btn`} className="relative mt-3 w-full h-11 rounded-full bg-white text-rose-600 font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-60 active:scale-[0.98] transition-transform">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {c.done ? `+${c.bonus} Bonus abholen` : `+${c.bonus} Bonuspunkte`}
          </button>}
    </div>
  );
}
